
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from 'src/components/docs/ContentSwitcher';
export const metadata = {
  toc: true,
  subnav: true,
  maxTocLevel: 5,
  layout: 'reference',
  secondLevelToc: true,
  parentTocLevel: 3,
  childTocLevel: 4,
  description: 'Plaid Core Exchange API Reference',
  title: 'Plaid Core Exchange API Reference',
  section: '4.6',
  source: 'endpoint'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Schema = makeShortcode("Schema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Core Exchange v4.6 API Reference' subtitle={metadata.description} mdxType="Header" />
    <h3 {...{
      "id": "overview"
    }}>{`Overview`}</h3>
    <p>{`When your organization builds Core Exchange, you implement a Financial Data Exchange (FDX)-aligned API.
This API handles GET HTTP requests that the Plaid network (or other data networks that you partner with) send.
These requests are for information about your customers' financial accounts.
Please note that this specification is subject to change as we make improvements to the product.
If you have questions, please reach out to the `}<a parentName="p" {...{
        "href": "mailto:dataconnectivity@plaid.com"
      }}>{`Plaid team`}</a>{`.`}</p>
    <h4 {...{
      "id": "endpoints"
    }}>{`Endpoints`}</h4>
    <p>{`This specification contains the following endpoints:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoints`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#customerscurrent"
            }}><inlineCode parentName="a">{`/customers/current`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get the ID of the customer within the authorization scope`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#accounts"
            }}><inlineCode parentName="a">{`/accounts`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Search and view customer accounts`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#accountsaccountid"
            }}><inlineCode parentName="a">{`/accounts/{accountId}`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get account balances, liabilities, and other information`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#accountsaccountidcontact"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/contact`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get account contact information`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#accountsaccountidpayment-networks"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/payment-networks`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get payment networks supported by an account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "#accountsaccountidtransactions"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/transactions`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List all account transactions`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "plaid-integration-and-interoperability"
    }}>{`Plaid integration and interoperability`}</h4>
    <p>{`The Core Exchange specifications describe the subset of FDX endpoints required by Plaid, as well as the subset of FDX information Plaid consumes
from each endpoint. Plaid takes the following approach to documenting these subsets:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`ENUMS`}</strong>{`: In cases where an endpoint describes FDX enums that Plaid doesn't consume, the specifications note the enums Plaid consumes, but refrains from removing unused enums.
This enables your organization to more easily integrate your Core Exchange implementation with other data networks.
For example, FDX defines many account type enums, so the specifications retain these enums but note which account type enums Plaid consumes in the `}<inlineCode parentName="p">{`GET /accounts/{accountId}`}</inlineCode>{` endpoint reference.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`SCHEMAS`}</strong>{`: In cases where an endpoint describes FDX schemas that Plaid doesn't consume, the specification `}<em parentName="p">{`omits`}</em>{` the schema.`}</p>
      </li>
    </ul>
    <h4 {...{
      "id": "fdx-compliance"
    }}>{`FDX compliance`}</h4>
    <p>{`The Core Exchange API specifications are a subset of the Financial Data Exchange (FDX) API specification, the usage thereof (or any part thereof) constitutes acceptance of the FDX API
License Agreement, which can be found at `}<a parentName="p" {...{
        "href": "https://financialdataexchange.org/"
      }}>{`https://financialdataexchange.org/`}</a>{`. The FDX API specification is distributed exclusively by FDX. Modifications to eliminate required or
conditional elements prescribed in the FDX API Certification Use Cases will render any implementations using said modifications non-conformant with the FDX API Certification Use Cases.
Please note that building the FDX-compliant Core Exchange API and permitting Plaid to call your build constitutes acceptance of
the FDX end user license agreement, which can be found at `}<a parentName="p" {...{
        "href": "https://financialdataexchange.org/"
      }}>{`https://financialdataexchange.org/`}</a>{`.
The full FDX API standard specification is distributed exclusively by FDX.`}</p>
    <h4 {...{
      "id": "download-the-specification"
    }}>{`Download the specification`}</h4>
    <p>{`To view this specification and documentation as an OpenAPI YAML file, see `}<a parentName="p" {...{
        "href": "https://github.com/plaid/core-exchange/tree/main/versions"
      }}>{`the public Core Exchange Github repository`}</a>{`.`}</p>
    <h4 {...{
      "id": "errors"
    }}>{`Errors`}</h4>
    <h5 {...{
      "id": "error-responses"
    }}>{`Error responses`}</h5>
    <p>{`An error entity which can be used at the API level for error responses or at the account level to indicate a problem specific to a particular account.
Error responses must contain a `}<inlineCode parentName="p">{`code`}</inlineCode>{` and `}<inlineCode parentName="p">{`message`}</inlineCode>{`, and may optionally include a `}<inlineCode parentName="p">{`debugMessage`}</inlineCode>{`.
See the descriptions below:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`code`}</inlineCode>{`: The FDX error code: A long-term persistent identifier which can be used to trace error condition back to log information.
(`}<strong parentName="li">{`Note:`}</strong>{` This code may differ from the HTTP status code.)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`message`}</inlineCode>{`: End user displayable information which might help the customer diagnose an error`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`debugMessage`}</inlineCode>{`: Message used to debug the root cause of the error. Contents should not be used in consumer's business logic. Can change at any time and should only be used for consumer to communicate with the data provider about an issue. Provider can include an error GUID in message for their use`}</li>
    </ul>
    <h5 {...{
      "id": "example-error"
    }}>{`Example error`}</h5>
    <p>{`The following is an example response with an HTTP status code of 409.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "code": 705,
  "message": "Account is closed",
  "debugMessage": "Operation is not supported by the closed account"
}
`}</code></pre>
    <p>{`See the Errors table in each endpoint description for more specific message and debug message examples.`}</p>
    <h3 {...{
      "id": "endpoints-1"
    }}>{`Endpoints`}</h3>
    <h4 {...{
      "id": "customerscurrent"
    }}><inlineCode parentName="h4">{`/customers/current`}</inlineCode></h4>
    <Schema method='GET' route='/customers/current' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`curl -X GET 'https://api.your-org.com/fdx/v4/customers/current' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok"
    }}><inlineCode parentName="h6">{`200 OK`}</inlineCode></h6>
    <Schema method='GET' route='/customers/current' type='response' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "accounts"
    }}><inlineCode parentName="h4">{`/accounts`}</inlineCode></h4>
    <Schema method='GET' route='/accounts' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`curl -X GET 'https://api.your-org.com/fdx/v4/accounts?offset=15&limit=50' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok-1"
    }}>{`200 OK`}</h6>
    <Schema method='GET' route='/accounts' type='response' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "accountsaccountid"
    }}><inlineCode parentName="h4">{`/accounts/{accountId}`}</inlineCode></h4>
    <Schema method='GET' route='/accounts/{accountId}' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`curl -X GET 'https://api.your-org.com/fdx/v4/accounts/{accountId}' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok-2"
    }}>{`200 OK`}</h6>
    <p>{`A 200 response will return one of the following types of `}<a parentName="p" {...{
        "href": "#account-objects"
      }}>{`accounts`}</a>{`:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#deposit-account"
        }}>{`Deposit account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#investment-account"
        }}>{`Investment account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#line-of-credit-account"
        }}>{`Line of credit account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#loan-account"
        }}>{`Loan account`}</a></li>
    </ul>
    <Schema method='GET' route='/accounts/{accountId}' type='response' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "accountsaccountidcontact"
    }}><inlineCode parentName="h4">{`/accounts/{accountId}/contact`}</inlineCode></h4>
    <Schema method='GET' route='/accounts/{accountId}/contact' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`curl -X GET 'https://api.your-org.com/fdx/v4/accounts/{accountId}/contact' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok-3"
    }}><inlineCode parentName="h6">{`200 OK`}</inlineCode></h6>
    <Schema type='model' model='AccountContact' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "accountsaccountidpayment-networks"
    }}><inlineCode parentName="h4">{`/accounts/{accountId}/payment-networks`}</inlineCode></h4>
    <Schema method='GET' route='/accounts/{accountId}/payment-networks' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`
curl -X GET 'https://api.your-org.com/fdx/v4/accounts/{accountId}/payment-networks?offset=50' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok-4"
    }}>{`200 OK`}</h6>
    <Schema method='GET' route='/accounts/{accountId}/payment-networks' type='response' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "accountsaccountidtransactions"
    }}><inlineCode parentName="h4">{`/accounts/{accountId}/transactions`}</inlineCode></h4>
    <Schema method='GET' route='/accounts/{accountId}/transactions' type='request' coreExchangeSpec={metadata.section} mdxType="Schema">
      <pre><code parentName="pre" {...{
          "className": "language-json"
        }}>{`
curl -X GET 'https://api.your-org.com/fdx/v4/accounts/{accountId}/transactions?startTime=2022-11-01&limit=100' \\
-H 'Content-Type: application/json' \\
-H 'Authorization: Bearer <access_token>'
`}</code></pre>
    </Schema>
    <h6 {...{
      "id": "200-ok-5"
    }}>{`200 OK`}</h6>
    <Schema method='GET' route='/accounts/{accountId}/transactions' type='response' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h3 {...{
      "id": "data-definitions"
    }}>{`Data definitions`}</h3>
    <h4 {...{
      "id": "account-objects"
    }}>{`Account objects`}</h4>
    <p>{`Account objects contain full details of each account type.
(`}<strong parentName="p">{`Note:`}</strong>{` Account objects differ from `}<a parentName="p" {...{
        "href": "#account-descriptors"
      }}>{`account descriptors`}</a>{`, which are lightweight objects that contain minimally necessary account details.)
The Core Exchange API supports the following accounts types:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#deposit-account"
        }}>{`Deposit account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#investment-account"
        }}>{`Investment account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#loan-account"
        }}>{`Loan account`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "#line-of-credit-account"
        }}>{`Line of credit account`}</a></li>
    </ul>
    <h5 {...{
      "id": "deposit-account"
    }}>{`Deposit account`}</h5>
    <Schema type='model' model='DepositAccount' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "investment-account"
    }}>{`Investment account`}</h5>
    <Schema type='model' model='InvestmentAccount' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "loan-account"
    }}>{`Loan account`}</h5>
    <Schema type='model' model='LoanAccount' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "line-of-credit-account"
    }}>{`Line of credit account`}</h5>
    <Schema type='model' model='LocAccount' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "account-descriptors"
    }}>{`Account descriptors`}</h4>
    <h5 {...{
      "id": "annuity-account-descriptor"
    }}>{`Annuity account descriptor`}</h5>
    <Schema type='model' model='AnnuityAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "deposit-account-descriptor"
    }}>{`Deposit account descriptor`}</h5>
    <Schema type='model' model='DepositAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "insurance-account-descriptor"
    }}>{`Insurance account descriptor`}</h5>
    <Schema type='model' model='InsuranceAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "investment-account-descriptor"
    }}>{`Investment account descriptor`}</h5>
    <Schema type='model' model='InvestmentAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "loan-account-descriptor"
    }}>{`Loan account descriptor`}</h5>
    <Schema type='model' model='LoanAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "line-of-credit-account-descriptor"
    }}>{`Line of credit account descriptor`}</h5>
    <Schema type='model' model='LocAccountDescriptor' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "account-statuses"
    }}>{`Account statuses`}</h4>
    <p>{`See the table below for help in selecting the appropriate account status.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CLOSED`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account that is closed or no longer exists`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DELINQUENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account with unpaid payments that are past due`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NEGATIVECURRENTBALANCE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account with a current negative balance`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OPEN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account that is open and active`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PAID`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account that is paid in full`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PENDINGCLOSE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account that is in the process of being closed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PENDINGOPEN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account that is in the process of being opened`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`RESTRICTED`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account with restricted access`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "account-types"
    }}>{`Account types`}</h4>
    <h5 {...{
      "id": "annuity-account-types"
    }}>{`Annuity account types`}</h5>
    <p><strong parentName="p">{`Discoverable-only`}</strong>{`: Annuity accounts are discoverable-only. Plaid does not consume downstream data (balances, transactions, Auth) for this account category. These accounts appear in the `}<inlineCode parentName="p">{`GET /accounts`}</inlineCode>{` response for discovery purposes only.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for annuity accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ANNUITY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A form of insurance or investment entitling the investor to a series of annual sums`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FIXEDANNUITY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of insurance contract that promises to pay the buyer a specific, guaranteed interest rate on their contributions to the account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`VARIABLEANNUITY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of insurance contract that promises to pay back the buyer based on the performance of an underlying portfolio of mutual funds selected by the buyer`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "deposit-account-types"
    }}>{`Deposit account types`}</h5>
    <p><strong parentName="p">{`Plaid consumption scope:`}</strong></p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Balances`}</strong>{`: Plaid returns balances for all deposit account types.`}</li>
      <li parentName="ul"><strong parentName="li">{`Auth`}</strong>{`: Plaid maps each account's `}<inlineCode parentName="li">{`accountType`}</inlineCode>{` to an internal subtype, and the mapping can vary by institution. By default, `}<inlineCode parentName="li">{`CHECKING`}</inlineCode>{` and `}<inlineCode parentName="li">{`SAVINGS`}</inlineCode>{` resolve to Auth-eligible subtypes (`}<inlineCode parentName="li">{`checking`}</inlineCode>{`, `}<inlineCode parentName="li">{`savings`}</inlineCode>{`, or `}<inlineCode parentName="li">{`cash management`}</inlineCode>{`); other types require Plaid to enable Auth for your institution before they return Auth data.`}</li>
      <li parentName="ul"><strong parentName="li">{`Transactions`}</strong>{`: Plaid consumes transactions for deposit accounts that resolve to Plaid's internal `}<inlineCode parentName="li">{`depository`}</inlineCode>{` account type. By default this includes `}<inlineCode parentName="li">{`CHECKING`}</inlineCode>{`, `}<inlineCode parentName="li">{`SAVINGS`}</inlineCode>{`, `}<inlineCode parentName="li">{`CD`}</inlineCode>{`, and `}<inlineCode parentName="li">{`MONEYMARKET`}</inlineCode>{`, though the mapping can vary by institution. Accounts that don't resolve to `}<inlineCode parentName="li">{`depository`}</inlineCode>{` return balances only (no Auth, no transactions).`}</li>
    </ul>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for deposit accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CHECKING`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A deposit account held at a financial institution that allows withdrawals and deposits`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SAVINGS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An interest-bearing deposit account held at a bank or other financial institution`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CD`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A certificate of deposit (CD) is a product offered by banks and credit unions that provides an interest rate premium in exchange for the customer agreeing to leave a lump-sum deposit untouched for a predetermined period of time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COMMERCIALDEPOSIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A deposit account for commercial customers, for example a business trust account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ESCROW`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A contractual arrangement in which a third party (the stakeholder or escrow agent) receives and disburses money or property for the primary transacting parties, with the disbursement dependent on conditions agreed to by the transacting parties`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`MONEYMARKET`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A deposit account that pays interest based on current interest rates in the money markets`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OTHERDEPOSIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Use when none of the listed enums apply`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "insurance-account-types"
    }}>{`Insurance account types`}</h5>
    <p><strong parentName="p">{`Discoverable-only`}</strong>{`: Insurance accounts are discoverable-only. Plaid does not consume downstream data (balances, transactions, Auth) for this account category. These accounts appear in the `}<inlineCode parentName="p">{`GET /accounts`}</inlineCode>{` response for discovery purposes only.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for insurance accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`LONGTERMDISABILITY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Insurance that replaces a portion of the policyholder's income due to a disability for an extended period of time, usually more than a year`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SHORTTERMDISABILITY`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Insurance that replaces a portion of the policyholder's income due to a disability for a short period of time, usually less than a year`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UNIVERSALLIFE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of a cash value life insurance where the excess of premium payments above the current cost of insurance is credited to the cash value of the policy, which in turn is credited each month with interest`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`WHOLELIFE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Life insurance which is guaranteed to remain in force for the insured's entire lifetime, provided required premiums are paid, or to the maturity date`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "investment-account-types"
    }}>{`Investment account types`}</h5>
    <p>{`Plaid consumes the same details for all investment accounts.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for investment accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`401A`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An employer-sponsored money-purchase retirement plan that allows dollar or percentage-based contributions from the employer, the employee, or both`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`401K`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An employer-sponsored defined-contribution pension account defined in subsection 401(k) of the Internal Revenue Code`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`403B`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A U.S. tax-advantaged retirement savings plan available for public education organizations, some non-profit employers (only Internal Revenue Code 501(c)(3) organizations), cooperative hospital service organizations, and self-employed ministers in the United States`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`529`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A tax-advantaged savings plan designed to help pay for education`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`BROKERAGEPRODUCT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Investment management offered by a licensed brokerage firm that places trades on behalf of the customer, utilizing any number of investment options`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COMMERCIALINVESTMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Investment Account for Commercial Customers. e.g. Commercial Brokerage Account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COVERDELL`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A trust or custodial account set up in the United States solely for paying qualified education expenses for the designated beneficiary of the account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIGITALASSET`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account containing digital assets`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DEFINEDBENEFIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An employer-sponsored retirement plan where employee benefits are computed using a formula that considers several factors, such as length of employment and salary history`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ESOP`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An employee stock ownership plan that gives employees an ownership interest in the company`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`GUARDIAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account of a child in the parent's name, with legal title to the assets in the account, as well as all capital gains and tax liabilities produced from the account belonging to the parent`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INSTITUTIONALTRUST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An institutional trust account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`IRA`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An individual retirement account (IRA) is a tax-advantaged account that individuals use to save and invest for retirement`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`KEOGH`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A tax-deferred pension plan available to self-employed individuals or unincorporated businesses for retirement purposes`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`NONQUALIFIEDPLAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of tax-deferred employer-sponsored retirement plan that falls outside of ERISA guidelines`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OTHERINVESTMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Use when none of the listed enums apply`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ROLLOVER`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account containing investments rolled over from an employee-sponsored account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ROTH`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An individual retirement account that offers tax-free growth and tax-free withdrawals in retirement`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SARSEP`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A simplified employee pension (SEP) plan set up before 1997 that includes a salary reduction arrangement`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TAXABLE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A taxable investment account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TDA`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A tax-deferred annuity, a type of retirement plan similar to a 403(b). Plaid maps this to the same account subtype as `}<inlineCode parentName="td">{`403B`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TRUST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of financial account that is opened by an individual and managed by a designated trustee for the benefit of a third party in accordance with agreed-upon terms`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TERM`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Life insurance that provides coverage at a fixed rate of payments for a limited period of time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UGMA`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Uniform Gifts to Minors Act account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`UTMA`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Uniform Transfers to Minors Act account`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "loan-account-types"
    }}>{`Loan account types`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for loan accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`AUTOLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of loan used to finance a car purchase`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COMMERCIALLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A loan used for business or commercial purposes`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`HOMEEQUITYLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of loan in which the borrower uses the equity of his or her home as collateral`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INSTALLMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of agreement or contract involving a loan that is repaid over time with a set number of scheduled payments`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`LOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The lending of money by one or more individuals, organizations, or other entities to other individuals, organizations etc. Use when none of the other loan enums apply`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`MILITARYLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A military loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`MORTGAGE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of loan you can use to buy or refinance a home`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PERSONALLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of debt that is not protected by a guarantor, or collateralized by a lien on specific assets of the borrower`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SMBLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A small/medium business loan`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`STUDENTLOAN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A type of loan designed to help students pay for post-secondary education and the associated fees, such as tuition, books and supplies, and living expenses`}</td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "line-of-credit-account-types"
    }}>{`Line of credit account types`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` field for line of credit accounts may be set to any of the following:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`LINEOFCREDIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A credit facility extended by a bank or other financial institution to a government, business or individual customer that enables the customer to draw on the facility when the customer needs funds. Use when none of the other line of credit enums apply`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CHARGE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account to which goods and services may be charged on credit`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`COMMERCIALLINEOFCREDIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An account with a preset borrowing limit that can be used at any time`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CREDITCARD`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Allows cardholders to borrow funds with which to pay for goods and services with merchants that accept cards for payment. Send credit cards as this type, not as a loan account`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`HOMELINEOFCREDIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A loan in which the lender agrees to lend a maximum amount within an agreed period, where the collateral is the borrower's equity in their house`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "transaction-objects"
    }}>{`Transaction objects`}</h4>
    <h5 {...{
      "id": "deposit-transaction"
    }}>{`Deposit transaction`}</h5>
    <Schema type='model' model='DepositTransaction' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "investment-transaction"
    }}>{`Investment transaction`}</h5>
    <Schema type='model' model='InvestmentTransaction' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "loan-transaction"
    }}>{`Loan transaction`}</h5>
    <Schema type='model' model='LoanTransaction' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h5 {...{
      "id": "line-of-credit-transaction"
    }}>{`Line of credit transaction`}</h5>
    <Schema type='model' model='LocTransaction' coreExchangeSpec={metadata.section} mdxType="Schema" />
    <h4 {...{
      "id": "transaction-types"
    }}>{`Transaction types`}</h4>
    <h5 {...{
      "id": "investment-transaction-types"
    }}>{`Investment transaction types`}</h5>
    <p>{`Plaid maps these enums to Plaid `}<a parentName="p" {...{
        "href": "https://plaid.com/docs/api/accounts/#investment-transaction-types-schema"
      }}>{`investment transaction types`}</a>{`, but not to Plaid-specific transaction subtypes.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Transaction type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}><a parentName="th" {...{
              "href": "https://plaid.com/docs/api/accounts/#investment-transaction-types-schema"
            }}>{`Investment transaction type`}</a></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ADJUSTMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fee`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ATM`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CASH`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CHECK`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CLOSURE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid recommends against using this value, rather specifying the closure type with any of the following: `}<inlineCode parentName="td">{`SOLDTOCLOSE`}</inlineCode>{`, `}<inlineCode parentName="td">{`PURCHASETOCLOSE`}</inlineCode>{`, `}<inlineCode parentName="td">{`OPTIONEXERCISE`}</inlineCode>{` or `}<inlineCode parentName="td">{`OPTIONEXPIRATION`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CLOSUREOPT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid recommends against using this value, rather specifying the closure type with any of the following: `}<inlineCode parentName="td">{`SOLDTOCLOSE`}</inlineCode>{`, `}<inlineCode parentName="td">{`PURCHASETOCLOSE`}</inlineCode>{`, `}<inlineCode parentName="td">{`OPTIONEXERCISE`}</inlineCode>{` or `}<inlineCode parentName="td">{`OPTIONEXPIRATION`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`CONTRIBUTION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode>{` (if transaction involves a security) or `}<inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DEP`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DEPOSIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIRECTDEBIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIRECTDEP`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIV`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIVIDEND`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`DIVIDENDREINVEST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`EXPENSE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FEE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fee`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INCOME`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INTEREST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INVEXPENSE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`JRNLFUND`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`JRNLSEC`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`MARGININTEREST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OPTIONEXERCISE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OPTIONEXPIRATION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`OTHER`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode>{` (unclassified)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PAYMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`POS`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PURCHASED`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PURCHASEDTOCOVER`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PURCHASETOCLOSE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PURCHASETOOPEN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`REINVESTOFINCOME`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`buy`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`REPEATPMT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`RETURNOFCAPITAL`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cash`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SOLD`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sell`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SOLDTOCLOSE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sell`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SOLDTOOPEN`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sell`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SPLIT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`SRVCHG`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fee`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`TRANSFER`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`XFER`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`transfer`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h5 {...{
      "id": "loan-transaction-types"
    }}>{`Loan transaction types`}</h5>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`ADJUSTMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Adjustment or correction`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`FEE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fee charge. For example, a late payment fee`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INTEREST`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Interest charge`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`PAYMENT`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Required payment that satisfies the minimum payment (e.g. principal + interest for mortgages)`}</td>
        </tr>
      </tbody>
    </table>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 3,
        title: "Overview",
      },
{
        id: "endpoints",
        level: 4,
        title: "Endpoints",
      },
{
        id: "plaid-integration-and-interoperability",
        level: 4,
        title: "Plaid integration and interoperability",
      },
{
        id: "fdx-compliance",
        level: 4,
        title: "FDX compliance",
      },
{
        id: "download-the-specification",
        level: 4,
        title: "Download the specification",
      },
{
        id: "errors",
        level: 4,
        title: "Errors",
      },
{
        id: "endpoints-1",
        level: 3,
        title: "Endpoints",
      },
{
        id: "customerscurrent",
        level: 4,
        title: "/customers/current",
      },
{
        id: "accounts",
        level: 4,
        title: "/accounts",
      },
{
        id: "accountsaccountid",
        level: 4,
        title: "/accounts/{accountId}",
      },
{
        id: "accountsaccountidcontact",
        level: 4,
        title: "/accounts/{accountId}/contact",
      },
{
        id: "accountsaccountidpayment-networks",
        level: 4,
        title: "/accounts/{accountId}/payment-networks",
      },
{
        id: "accountsaccountidtransactions",
        level: 4,
        title: "/accounts/{accountId}/transactions",
      },
{
        id: "data-definitions",
        level: 3,
        title: "Data definitions",
      },
{
        id: "account-objects",
        level: 4,
        title: "Account objects",
      },
{
        id: "account-descriptors",
        level: 4,
        title: "Account descriptors",
      },
{
        id: "account-statuses",
        level: 4,
        title: "Account statuses",
      },
{
        id: "account-types",
        level: 4,
        title: "Account types",
      },
{
        id: "transaction-objects",
        level: 4,
        title: "Transaction objects",
      },
{
        id: "transaction-types",
        level: 4,
        title: "Transaction types",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
